import { observer } from "mobx-react";
import * as React from "react";
import { useContext, useEffect, useCallback, useMemo, useState } from "react";
import { RootStoreContext } from "../../../stores/RootStore";
import { runInAction, observable } from "mobx";
import "./RegistrationSelectSellingItemsStep.css";
import { useSellingItems } from "./hooks/useSellingItems";
import { useCategoryItems } from "./hooks/useCategoryItems";
import SellingItemTabs from "./SellingItemTabs";
import DateSelector from "./DateSelector";
import NoClassesMessage from "./NoClassesMessage";
import { Event } from "../../../config/Registration";
import ItemsSection from "./ItemsSection";
import RentalSection from "./RentalSection";
import PrivateClasses from "../../../views/PrivateClasses";
import EventNotFound from "../../../views/EventNotFound";
import { StepName } from "../../../config/Wizard";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { parseEventId } from "../../../stores/EventDeepLinkSession";
import { isRentalTabReselection } from "./rentalPageState";
import { buildBookedDateKeys, eventOccursOnDate } from "./classesSchedule";

const RegistrationSelectSellingItemsStep = observer(() => {
  const { registrationStore, studioConfigStore, suggestionStore, wizardStore } = useContext(RootStoreContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId: eventIdParam } = useParams();
  const eventId = parseEventId(eventIdParam) ?? undefined;
  const openedFromClasses = Boolean((location.state as { fromClasses?: boolean } | null)?.fromClasses);
  const { user, activeCategory, setActiveCategory, selectedEventId  } = registrationStore;
  const [focusedLoadState, setFocusedLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [rentalPageVersion, setRentalPageVersion] = useState(0);

  // Get rental services from StudioConfigStore and make them observable
  const rentalServices = useMemo(() => 
    observable(
      studioConfigStore.getRoomRentServices().map(service => ({
        ...service,
        selected: false
      }))
    ),
    [studioConfigStore]
  );

  const {
    sellingItemCategories,
    categoryActiveItems,
    loadSellingItems,
    unselectAllSellingItems,
    unselectAllEventTickets,
  } = useSellingItems(registrationStore, user);

  const privateClassesAllowed = studioConfigStore.isPrivateClassesAllowed(user?.id);
  const privateClassesCategoryIndex = sellingItemCategories.length + (studioConfigStore.rentEnabled ? 1 : 0);
  const isPrivateClassesCategory = privateClassesAllowed && activeCategory === privateClassesCategoryIndex;

  const { filteredSellingItems, events, rentalServices: currentRentalServices, isRentalCategory } = useCategoryItems(
    sellingItemCategories,
    activeCategory,
    categoryActiveItems,
    rentalServices,
    studioConfigStore.rentEnabled
  ) as { filteredSellingItems: any[], events: Event[], rentalServices: any[], isRentalCategory: boolean };

  const isEventsCategory = sellingItemCategories && sellingItemCategories[activeCategory]?.name === "Events";
  const isClassesCategory = sellingItemCategories && sellingItemCategories[activeCategory]?.name === "Classes";
  const isNotEventsCategory = sellingItemCategories && sellingItemCategories[activeCategory]?.name !== "Events";
  const isEventSelectedOrNotEventsCategory = (!isEventsCategory || selectedEventId);
  const showConfirmButtonOnTop = !isEventsCategory && filteredSellingItems.length > 5;
  const showConfirmButtonOnBottom = isEventSelectedOrNotEventsCategory && filteredSellingItems.length > 0;

  const eventsCategory = sellingItemCategories?.find(category => category.name === "Events");
  const classScheduleEvents = useMemo(() => {
    if (!isClassesCategory) return [];
    return (eventsCategory?.events || [])
      .filter(event => eventOccursOnDate(event, studioConfigStore.selectedDate))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [eventsCategory?.events, isClassesCategory, studioConfigStore.selectedDate]);
  const bookedDateKeys = useMemo(
    () => buildBookedDateKeys(user?.bookingHistory || [], eventsCategory?.events || []),
    [eventsCategory?.events, user?.bookingHistory]
  );
  const deepLinkedEvent = eventId
    ? eventsCategory?.events?.find(event => event.eventId === eventId && event.tickets?.length > 0)
    : undefined;
  const selectedEvent = eventId ? deepLinkedEvent : events.find(event => event.eventId === selectedEventId);

  const handleEventSelect = useCallback((event: Event) => {
    runInAction(() => {
      registrationStore.selectedEventId = event.eventId;
    });
    filteredSellingItems.forEach(item => {
      if (item.selected) {
        runInAction(() => {
          item.selected = false;
        });
      }
    });
  }, [registrationStore, filteredSellingItems]);

  const handleClassEventSelect = useCallback((event: Event) => {
    navigate(`/event/${event.eventId}`, { state: { fromClasses: true } });
  }, [navigate]);

  const handleDateSelect = useCallback((date: Date) => {
    studioConfigStore.setSelectedDate(date);
    unselectAllSellingItems();
    unselectAllEventTickets();
    runInAction(() => {
      registrationStore.selectedEventId = null;
    });
    // Always force a data refresh when date changes
    loadSellingItems(date, true);
  }, [studioConfigStore, unselectAllSellingItems, unselectAllEventTickets, registrationStore, loadSellingItems]);

  useEffect(() => {
    // Load studio configs on component mount
    studioConfigStore.fetchStudioConfigs();
  }, [studioConfigStore]);

  useEffect(() => {
    // Load selling items with the selected date
    const dateToUse = studioConfigStore.shouldShowDateSelector ? studioConfigStore.selectedDate : undefined;
    let isActive = true;
    if (eventId) {
      setFocusedLoadState('loading');
    }

    const loadItems = async () => {
      const succeeded = await loadSellingItems(dateToUse, !!eventId);
      if (!isActive || !eventId) {
        return;
      }

      setFocusedLoadState(succeeded ? 'loaded' : 'error');
      if (!succeeded) {
        wizardStore.setStep(StepName.REGISTRATION_FAILED);
      }
    };

    loadItems();
    return () => {
      isActive = false;
    };
  }, [eventId, user, studioConfigStore.selectedDate, studioConfigStore.shouldShowDateSelector, loadSellingItems, wizardStore]);

  useEffect(() => {
    if (eventId && !openedFromClasses) {
      const eventsCategoryIndex = sellingItemCategories?.findIndex(
        category => category.name === "Events"
      );
      if (eventsCategoryIndex !== -1) {
        setActiveCategory(eventsCategoryIndex);
      }
    }
  }, [eventId, openedFromClasses, sellingItemCategories, setActiveCategory]);



  useEffect(() => {
    if (eventId && deepLinkedEvent) {
      handleEventSelect(deepLinkedEvent);
    }
  }, [eventId, deepLinkedEvent, handleEventSelect]);

  useEffect(() => {
    if (selectedEvent) {
      setTimeout(() => {
        window.scrollTo({ top: 0 });
      }, 100);
    }
  }, [selectedEvent]);

  // Trigger 2: when the Classes tab is the active view and the user is
  // authenticated, offer the early check-in suggestion dialog (honors the
  // 60-minute cooldown). The store self-gates on cooldown / empty results.
  useEffect(() => {
    if (isClassesCategory && user?.id) {
      suggestionStore.maybeShowOnClassesTab(user.id);
    }
  }, [isClassesCategory, user?.id, suggestionStore]);

  const handleCategoryChange = (category: number) => {
    if (isRentalTabReselection(
      category,
      activeCategory,
      sellingItemCategories.length,
      studioConfigStore.rentEnabled
    )) {
      studioConfigStore.resetSelectedDate();
      setRentalPageVersion(version => version + 1);
    }

    unselectAllEventTickets();
    unselectAllSellingItems();
    runInAction(() => {
      registrationStore.selectedEventId = null;
      // Deselect all rentals when changing category
      rentalServices.forEach(rental => {
        rental.selected = false;
      });
    }); 

    setActiveCategory(category);
  };

  const handleDeepLinkCategoryChange = (category: number) => {
    const eventsCategoryIndex = sellingItemCategories.findIndex(item => item.name === "Events");
    handleCategoryChange(category);

    if (category !== eventsCategoryIndex) {
      navigate('/');
    }
  };

  if (eventId) {
    if (focusedLoadState !== 'loaded') {
      return null;
    }

    if (!deepLinkedEvent) {
      return <EventNotFound />;
    }

    return (
      <>
        <SellingItemTabs
          value={activeCategory}
          onChange={handleDeepLinkCategoryChange}
          categories={sellingItemCategories}
          hasRentalTab={studioConfigStore.rentEnabled}
          hasPrivateClassesTab={privateClassesAllowed}
        />
        <ItemsSection
          showConfirmButtonOnTop={false}
          isEventsCategory={true}
          selectedEvent={deepLinkedEvent}
          events={[]}
          onEventSelect={handleEventSelect}
          isNotEventsCategory={false}
          filteredSellingItems={[]}
          showConfirmButtonOnBottom={false}
          activeCategory={activeCategory}
          isClassesCategory={false}
        />
      </>
    );
  }

  return (
    <>
      <SellingItemTabs 
        value={activeCategory} 
        onChange={handleCategoryChange} 
        categories={sellingItemCategories}
        hasRentalTab={studioConfigStore.rentEnabled}
        hasPrivateClassesTab={privateClassesAllowed}
      />

      {isClassesCategory && studioConfigStore.shouldShowDateSelector && (
        <DateSelector
          onDateSelect={handleDateSelect}
          checkInFutureDays={studioConfigStore.checkInFutureDays}
          displayMode="strip"
          bookedDateKeys={bookedDateKeys}
        />
      )}

      {isClassesCategory && filteredSellingItems.length === 0 && classScheduleEvents.length === 0 && (
        <NoClassesMessage 
          selectedDate={studioConfigStore.selectedDate}
        />
      )}
      
      {isPrivateClassesCategory ? (
        <PrivateClasses embedded onExit={() => handleCategoryChange(0)} />
      ) : isRentalCategory ? (
        <RentalSection
          key={`rental-page-${rentalPageVersion}`}
          rentalServices={currentRentalServices}
        />
      ) : (
        (filteredSellingItems.length > 0 || classScheduleEvents.length > 0 || !isClassesCategory) && (
          <ItemsSection
            showConfirmButtonOnTop={showConfirmButtonOnTop}
            isEventsCategory={isEventsCategory}
            selectedEvent={selectedEvent}
            events={events}
            onEventSelect={handleEventSelect}
            isNotEventsCategory={isNotEventsCategory}
            filteredSellingItems={filteredSellingItems}
            showConfirmButtonOnBottom={showConfirmButtonOnBottom}
            activeCategory={activeCategory}
            isClassesCategory={isClassesCategory}
            classScheduleEvents={classScheduleEvents}
            selectedDate={studioConfigStore.selectedDate}
            onClassEventSelect={handleClassEventSelect}
          />
        )
      )}
    </>
  );
});

export default RegistrationSelectSellingItemsStep;
